/**
 * Ölçüm raporu.
 *
 * Cümleleri bu ekran kurmuyor: hepsi @nirengi/engine içindeki rapor
 * katmanından geliyor. Böylece çocuğun cihazındaki özet ile psikoloğun
 * gördüğü rapor aynı dili konuşuyor — iki yerde iki ayrı yorum yazmak
 * zamanla iki ayrı ürün demek.
 */

import { useEffect, useState } from 'react'
import {
  BIRIM,
  DOMAIN_LABEL,
  DOMAIN_ORDER,
  KISITLAR,
  TASK_ADI,
  bicim,
  metricLabel,
  yorumla,
  type Domain,
  type ScoreRow,
  type TaskCode,
} from '@nirengi/engine'
import { api, type OlcumSonucu } from '../api'
import { DenemeDetayi } from './DenemeDetayi'

function tarih(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('tr-TR', { dateStyle: 'long', timeStyle: 'short' })
}

export function Rapor({ olcumId, onGeri }: { olcumId: string; onGeri: () => void }) {
  const [sonuc, setSonuc] = useState<OlcumSonucu | null>(null)
  const [kisi, setKisi] = useState<string | null>(null)
  const [hata, setHata] = useState<string | null>(null)

  useEffect(() => {
    let iptal = false
    api
      .olcum(olcumId)
      .then(async (r) => {
        if (iptal) return
        setSonuc(r)
        try {
          const d = await api.danisan(r.olcum.clientId)
          const k = d.danisan as Record<string, string | null>
          if (!iptal) setKisi(`${k['firstName'] ?? ''} ${k['lastName'] ?? ''} · ${k['fileNo'] ?? ''}`)
        } catch {
          /* başlık olmadan da rapor okunur */
        }
      })
      .catch(() => {
        if (!iptal) setHata('Rapor alınamadı.')
      })
    return () => {
      iptal = true
    }
  }, [olcumId])

  if (hata) {
    return (
      <>
        <div className="uyari">{hata}</div>
        <button onClick={onGeri}>← Geri</button>
      </>
    )
  }
  if (!sonuc) return <p className="alt">Yükleniyor…</p>

  /** Yorum katmanı sayı bekliyor; numeric JSON'a metin olarak geliyor */
  const satirlar: ScoreRow[] = sonuc.skorlar.map((s) => ({
    domain: s.domain as Domain,
    task: (s.taskCode ?? undefined) as TaskCode | undefined,
    metric: s.metric as ScoreRow['metric'],
    value: Number(s.value),
    unit: s.unit as ScoreRow['unit'],
    trialsUsed: s.trialsUsed,
  }))

  const dolu = DOMAIN_ORDER.filter((d) => satirlar.some((s) => s.domain === d))

  return (
    <>
      <div className="dugmeler" style={{ marginBottom: 12 }}>
        <button onClick={onGeri}>← Dosya</button>
        <button onClick={() => window.print()}>Yazdır</button>
      </div>

      <h1>Bilişsel ölçüm raporu</h1>
      <p className="alt">
        {kisi ? kisi + ' · ' : ''}
        {sonuc.olcum.instrumentCode} s{sonuc.olcum.instrumentVersion} · Form {sonuc.olcum.form} ·{' '}
        {tarih(sonuc.olcum.completedAt ?? sonuc.olcum.startedAt)} · {sonuc.denemeSayisi} deneme
      </p>

      {sonuc.olcum.invalidatedReason && (
        <div className="uyari">
          Geçerlilik uyarısı: denemelerin bir kısmı ölçüme alınamadı
          {sonuc.olcum.invalidatedReason.startsWith('gecersiz_oran_')
            ? ` (yaklaşık %${sonuc.olcum.invalidatedReason.replace('gecersiz_oran_', '')})`
            : ''}
          . Sonuçları yorumlamadan önce ölçümün tekrarlanması gerekir.
        </div>
      )}

      {sonuc.olcum.status !== 'completed' && (
        <div className="bilgi">Bu ölçüm tamamlanmamış; aşağıdaki sayılar eksik veriden üretildi.</div>
      )}

      {dolu.length === 0 ? (
        <div className="bilgi">Bu ölçümden puanlanabilir veri çıkmadı.</div>
      ) : (
        dolu.map((d) => {
          const alanSatirlari = satirlar.filter((s) => s.domain === d)
          const gorevler = [...new Set(alanSatirlari.map((s) => s.task).filter(Boolean))] as TaskCode[]
          const enAzDeneme = Math.min(...alanSatirlari.map((s) => s.trialsUsed))
          return (
            <section className="alan" key={d}>
              <div className="alan-ad">{DOMAIN_LABEL[d]}</div>
              <div className="alan-yorum">{yorumla(d, satirlar)}</div>
              <div className="alan-olcut">
                {alanSatirlari.map((s) => (
                  <span key={s.metric}>
                    {metricLabel(s.metric, s.task)}:{' '}
                    <strong style={{ color: 'var(--ink)' }}>
                      {bicim(s.value, s.unit, s.metric)}
                      {BIRIM[s.unit] ? ' ' + BIRIM[s.unit] : ''}
                    </strong>
                  </span>
                ))}
              </div>
              <div className="alan-kaynak">
                {gorevler.length > 0
                  ? 'Kaynak: ' + gorevler.map((g) => TASK_ADI[g]).join(', ')
                  : 'Kaynak görev kaydı yok (bu ölçüm görev bilgisi eklenmeden puanlandı).'}
                {' · '}
                {enAzDeneme} geçerli deneme
              </div>
              {gorevler.map((g) => (
                <DenemeDetayi key={g} olcumId={olcumId} gorev={g} />
              ))}
            </section>
          )
        })
      )}

      <div className="kisit">{KISITLAR}</div>

      <p className="alt" style={{ marginTop: 16 }}>
        Puanlama sürümü {sonuc.skorlar[0]?.scoringVersion ?? '—'}
        {sonuc.olcum.measuredRefreshHz
          ? ` · ekran ${Math.round(Number(sonuc.olcum.measuredRefreshHz))} Hz`
          : ''}
        . Ham veri saklanıyor; norm çalışması yayımlandığında bu ölçüm yeniden puanlanabilir.
      </p>
    </>
  )
}
